import { SalesRecordingAggregateStatus, SalesRecordingStatus } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { Chip, Tooltip, useTheme } from '@mui/material';
import React from 'react';

interface StatusChipProps {
  status: SalesRecordingAggregateStatus | SalesRecordingStatus;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  showTooltip?: boolean;
  tooltipCustomMessage?: string;
}

export const StatusChip = ({
  status,
  onClick = undefined,
  showTooltip = false,
  tooltipCustomMessage = '',
}: StatusChipProps) => {
  let color = '';
  let label = '';
  let tooltipMessage = '';
  let variant: 'outlined' | 'filled' = 'filled';
  const theme = useTheme();

  switch (status) {
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusApproved:
    case SalesRecordingStatus.VerifiedStatus:
      color = theme.palette.common.success10;
      label = 'Approved';
      tooltipMessage = 'Unapprove this Record';
      break;
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusUnapproved:
    case SalesRecordingStatus.SoldStatus:
      color = 'default';
      label = 'Unapproved';
      variant = 'outlined';
      tooltipMessage = 'Approve this Record';
      break;
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusInvoiced:
    case SalesRecordingStatus.InvoicedStatus:
      color = theme.palette.common.info10;
      label = 'Invoiced';
      tooltipMessage = 'This has been approved and invoiced already';
      break;
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusProcessing:
    case SalesRecordingStatus.ProcessingStatus:
      color = theme.palette.common.white;
      label = 'Processing...';
      break;
    case SalesRecordingAggregateStatus.SalesRecordingAggregateStatusError:
    case SalesRecordingStatus.ErrorStatus:
      color = theme.palette.common.error10;
      label = 'Error';
      tooltipMessage = 'Not exported to Quickbooks';
      break;
    default:
      color = 'default';
      label = 'Unknown';
      tooltipMessage = '';
  }

  const handleChipClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (onClick) onClick(event);
  };

  const chip = (
    <Chip
      label={label}
      variant={variant}
      onClick={onClick ? handleChipClick : undefined}
      sx={{
        backgroundColor: color,
        color: variant === 'outlined' ? theme.palette.text.primary : '',
        ...(variant === 'outlined' && { border: `1px solid ${theme.palette.divider}` }),
        cursor: onClick ? 'pointer' : 'default',
      }}
    />
  );

  return showTooltip && tooltipMessage ? (
    <Tooltip title={tooltipCustomMessage ? `${tooltipMessage}: ${tooltipCustomMessage}` : tooltipMessage} placement="top">
      {chip}
    </Tooltip>
  ) : chip;
};

export const formatDateToYYYYMM = (date: Date) => `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;

export const parseDateFromYYYYMM = (dateString: string) => {
  const [year, month] = dateString.split('-').map(Number);
  return new Date(year, month - 1, 1);
};
